<template>
    <ModalContainer
        :wide="showPayment"
        :isOpened="isPaymentModalOpen"
        @closeModal="onCloseModal"
    >
        <template #custom-content>
            <div :class="$style.row">
                <div :class="[$style.column, $style.info]">
                    <TopUpBlock
                        v-if="order?.type === 'topup'"
                        :amount="localAmount"
                        :minPurchase="minPurchase"
                        @update:amount="onAmountChange"
                    />
                    <OrderBlock
                        v-if="order?.type === 'order'"
                        :order="order"
                        :amount="localAmount"
                        :balance="balance"
                        :showPayment="showPayment"
                    />
                    <DisableAdOrderBlock
                        v-if="order?.type === 'disable-ad-order'"
                        :order="order"
                        :balance="balance"
                        :showPayment="showPayment"
                    />
                    <BalanceBlock
                        :balance="balance"
                        :showPayment="showPayment"
                    />
                    <Button
                        v-if="!showPayment"
                        :disabled="isPurchaseDisabled"
                        width="full"
                        size="l"
                        color="golden"
                        interfaceTarget="buy-golden-coins-button"
                        @click="purchase"
                    >
                        {{ $tr('paymentModal.purchaseButton') }}
                    </Button>
                </div>
                <div
                    v-if="showPayment"
                    :class="[$style.column, $style.iframe]"
                >
                    <iframe
                        v-if="tokenReady"
                        :src="xsollaIframeSrc"
                        width="100%"
                        height="600"
                        allow="clipboard-read; clipboard-write; payment"
                    />
                    <Icon
                        v-if="!tokenReady"
                        name="loader"
                        :class="$style.loader"
                    />
                </div>
            </div>
        </template>
    </ModalContainer>
</template>
<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import Button from '@/components_new/Button.vue'
import Icon from '@/components_new/Icon/Icon.vue'
import ModalContainer from '@/components_new/ModalItems/ModalContainer.vue'
import { useConfig } from '@/composables/config'
import { processPurchase } from '@/modules/in-game-payments'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { request } from '@/utils/requests/request'

import BalanceBlock from './components/BalanceBlock.vue'
import DisableAdOrderBlock from './components/DisableAdOrderBlock.vue'
import OrderBlock from './components/OrderBlock.vue'
import TopUpBlock from './components/TopUpBlock.vue'

const layoutStore = useLayoutStore()
const userStore = useUserStore()
const loggerStore = useLoggerStore()

const { isPaymentModalOpen, paymentModalOrder } = storeToRefs(layoutStore)
const { user } = storeToRefs(userStore)

const xsollaIframeToken = ref('')
const abortRef = ref()
const xsollaIframeSrc = computed(() => `${VUE_APP_XSOLLA_IFRAME_URL}/?token=${xsollaIframeToken.value}`)
const tokenReady = computed(() => Boolean(xsollaIframeToken.value))
const balance = computed(() => user.value?.goldFenecBalance || 0)
const showPayment = computed(
    () =>
        paymentModalOrder.value.order?.type === 'topup' || (paymentModalOrder.value.order?.amount || 0) > balance.value,
)
const order = computed(() => paymentModalOrder.value.order)
const { VUE_APP_XSOLLA_IFRAME_URL, VUE_APP_MINIMUM_PURCHASE } = useConfig()
const minPurchase = computed(() => (order.value?.type === 'disable-ad-order' ? 0 : parseInt(VUE_APP_MINIMUM_PURCHASE)))

const localAmount = ref(paymentModalOrder.value.order?.amount || 0)
const updateAmount = (val: number) => {
    localAmount.value = val
    // This trick allows Vue to render the incorrect amount first and then the correct
    // Otherwise input value stays wrong
    setTimeout(() => {
        if (localAmount.value < minPurchase.value) {
            localAmount.value = minPurchase.value
        }
    }, 0)
}
const debouncedUpdateAmount = useDebounceFn(updateAmount, 200)
const onAmountChange = (val: number) => {
    xsollaIframeToken.value = ''
    if (abortRef.value) {
        abortRef.value.abort('New request sent')
        abortRef.value = undefined
    }
    debouncedUpdateAmount(val)
}
watch(order, (val) => {
    if (!val) {
        localAmount.value = 0
        return
    }

    const neededAmount = val.type === 'topup' ? minPurchase.value : val.amount - balance.value
    onAmountChange(neededAmount)
})

watch(localAmount, async (amount) => {
    if (amount >= minPurchase.value) {
        abortRef.value = new AbortController()
        try {
            const res = await request('/api/v1/in-game-payments/createXsollaToken', {
                method: 'POST',
                // Uncomment this locally
                /*
                headers: {
                    'content-type': 'application/json',
                    'cf-ipcountry': 'ES',
                },
                */
                body: JSON.stringify({ amount }),
                signal: abortRef.value.signal,
            })

            if (res.errors.length === 0) {
                xsollaIframeToken.value = res.data
            }
        } catch (e) {
            console.error(e)
        } finally {
            abortRef.value = undefined
        }
    }
})
const isPurchaseDisabled = ref(false)

function onCloseModal() {
    layoutStore.paymentModalOrder.reject?.(new Error(`User didn't complete payment`))
    layoutStore.setPaymentModalOrder(null)
    xsollaIframeToken.value = ''
}

async function purchase() {
    try {
        isPurchaseDisabled.value = true

        if (order.value?.type !== 'order' && order.value?.type !== 'disable-ad-order') {
            throw new Error('Invalid order type for purchase')
        }

        const { adPlacement, ...orderObj } = order.value

        const purchaseStatus = await processPurchase(
            orderObj,
            orderObj.type === 'disable-ad-order' ? '/api/v1/user/disable-ad' : undefined,
        )

        if (purchaseStatus.errors.length > 0) {
            throw new Error(purchaseStatus.errors.join(', '))
        }

        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'purchase_successful',
            action: orderObj.type,
            amountInGameCurrency: orderObj.amount,
            adPlacement,
        })

        // Update balance
        await userStore.fetchUserInfo()
        layoutStore.paymentModalOrder.resolve?.(purchaseStatus)
    } catch (err) {
        layoutStore.paymentModalOrder.reject?.(err)
    } finally {
        isPurchaseDisabled.value = false
        layoutStore.setPaymentModalOrder(null)
    }
}

async function modalEventListener(event: MessageEvent) {
    if (!VUE_APP_XSOLLA_IFRAME_URL.includes(event.origin)) {
        return
    }

    let eventData: { command: string; data?: string }
    try {
        eventData = JSON.parse(event.data)
    } catch (e) {
        console.error('Failed to parse event data', e)
        return
    }

    try {
        if (eventData.command !== 'open-status-success') {
            return
        }

        const userInfo = await userStore.fetchUserInfo()

        if (order.value?.type === 'topup') {
            layoutStore.paymentModalOrder.resolve?.(userInfo)
        }

        if (order.value?.type === 'order' || order.value?.type === 'disable-ad-order') {
            purchase()
        }
    } catch (e) {
        layoutStore.paymentModalOrder.reject?.(e)
    }
}

const route = useRoute()

watch(
    () => route.fullPath,
    () => {
        layoutStore.paymentModalOrder.reject?.(new Error('User left game page'))
        isPurchaseDisabled.value = false
        layoutStore.setPaymentModalOrder(null)
    },
)

watch(
    () => [isPaymentModalOpen.value, paymentModalOrder.value.order] as const,
    ([isOpen, o]) => {
        if (isOpen && o) {
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'payment_modal_opened',
                action: o.type,
                adPlacement: 'adPlacement' in o ? o.adPlacement : undefined,
            })
        }
    },
)

onMounted(() => {
    window.addEventListener('message', modalEventListener)
})
onUnmounted(() => {
    window.removeEventListener('message', modalEventListener)
})
</script>

<style module>
.row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
}

.column {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.iframe {
    width: 100%;
    min-width: 342px;
    min-height: 600px;
    background-color: rgb(44, 38, 64);
    position: relative;
}

.loader {
    position: absolute;
    top: 200px;
    left: 191px;
}

.info {
    padding-right: 10px;
}

@media (--desktop-small-plus) {
    .iframe {
        width: 50%;
    }
}
</style>
